import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Send, Mail } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { API, CONTACT, IMAGES } from "@/constants/config";
import { useLang } from "@/constants/i18n";

const EMPTY = { nombre: "", telefono: "", email: "", mensaje: "" };

const inputClass =
    "w-full border-b border-ink/20 bg-transparent py-3 text-base text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-burgundy";

export const Contact = () => {
    const { t, lang } = useLang();
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/leads`, { ...form, lang });
            toast.success(t.contact.toastSuccess);
            setForm(EMPTY);
        } catch (err) {
            toast.error(t.contact.toastError);
        } finally {
            setSending(false);
        }
    };

    const options = [
        {
            id: "contact-phone-link",
            href: CONTACT.phoneHref,
            icon: Phone,
            title: CONTACT.phoneDisplay,
            sub: t.contact.phoneSub,
            external: false,
            delay: 0.28,
        },
        {
            id: "contact-whatsapp-link",
            href: CONTACT.whatsappUrl,
            icon: MessageCircle,
            title: "WhatsApp",
            sub: t.contact.whatsappSub,
            external: true,
            delay: 0.36,
        },
        {
            id: "contact-email-link",
            href: CONTACT.emailHref,
            icon: Mail,
            title: CONTACT.email,
            sub: t.contact.emailSub,
            external: false,
            delay: 0.44,
        },
    ];

    return (
        <section
            id="contacto"
            ref={ref}
            data-testid="contact-section"
            className="relative overflow-hidden bg-burgundy py-24 text-ivory md:py-32"
        >
            <motion.div
                style={{ y: bgY }}
                className="pointer-events-none absolute inset-[-15%] opacity-25"
            >
                <img
                    src={IMAGES.ctaBg}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                />
            </motion.div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-burgundy-deep/70 via-burgundy/40 to-burgundy-deep/80" />

            <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 md:px-10 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <SectionHeading
                        number="06"
                        eyebrow={t.contact.eyebrow}
                        title={t.contact.title}
                        dark
                    />
                    <Reveal delay={0.15}>
                        <p className="max-w-lg text-base leading-relaxed text-ivory/75 md:text-lg">
                            {t.contact.intro}
                        </p>
                    </Reveal>
                    <div className="mt-12 space-y-4">
                        {options.map((opt) => (
                            <Reveal key={opt.id} delay={opt.delay}>
                                <a
                                    href={opt.href}
                                    {...(opt.external
                                        ? { target: "_blank", rel: "noopener noreferrer" }
                                        : {})}
                                    data-testid={opt.id}
                                    className="group flex items-center gap-5 border border-gold/30 bg-ivory/5 px-6 py-5 backdrop-blur-sm transition-[background-color,border-color] duration-300 hover:border-gold hover:bg-ivory/10"
                                >
                                    <opt.icon className="h-6 w-6 shrink-0 text-gold" />
                                    <div className="flex-1">
                                        <p className="break-all font-serif text-xl">{opt.title}</p>
                                        <p className="text-sm text-ivory/60">{opt.sub}</p>
                                    </div>
                                    <Send className="h-4 w-4 text-ivory/40 transition-transform duration-300 group-hover:translate-x-1" />
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <div className="flex items-center lg:col-span-6">
                    <Reveal delay={0.25} className="w-full">
                        <form
                            onSubmit={submit}
                            data-testid="lead-form"
                            className="border border-gold/30 bg-ivory p-8 text-ink shadow-2xl shadow-burgundy-deep/50 md:p-10"
                        >
                            <h3 className="font-serif text-3xl font-light">
                                {t.contact.form.title}
                            </h3>
                            <p className="mt-2 text-sm text-ink/60">{t.contact.form.sub}</p>
                            <div className="mt-8 space-y-6">
                                <div>
                                    <label htmlFor="lead-nombre" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                                        {t.contact.form.labelNombre}
                                    </label>
                                    <input
                                        id="lead-nombre"
                                        data-testid="lead-nombre-input"
                                        required
                                        minLength={2}
                                        value={form.nombre}
                                        onChange={set("nombre")}
                                        placeholder={t.contact.form.phNombre}
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lead-telefono" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                                        {t.contact.form.labelTelefono}
                                    </label>
                                    <input
                                        id="lead-telefono"
                                        data-testid="lead-telefono-input"
                                        required
                                        minLength={7}
                                        type="tel"
                                        value={form.telefono}
                                        onChange={set("telefono")}
                                        placeholder={t.contact.form.phTelefono}
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lead-email" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                                        {t.contact.form.labelEmail}
                                    </label>
                                    <input
                                        id="lead-email"
                                        data-testid="lead-email-input"
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder={t.contact.form.phEmail}
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lead-mensaje" className="text-xs uppercase tracking-[0.25em] text-ink/60">
                                        {t.contact.form.labelMensaje}
                                    </label>
                                    <textarea
                                        id="lead-mensaje"
                                        data-testid="lead-mensaje-input"
                                        rows={3}
                                        value={form.mensaje}
                                        onChange={set("mensaje")}
                                        placeholder={t.contact.form.phMensaje}
                                        className={`${inputClass} resize-none`}
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="lead-submit-button"
                                className="mt-10 w-full rounded-full bg-burgundy py-4 text-sm tracking-wide text-ivory transition-[background-color,transform,opacity] duration-300 hover:bg-forest active:scale-[0.98] disabled:opacity-60"
                            >
                                {sending ? t.contact.form.sending : t.contact.form.submit}
                            </button>
                            <p className="mt-4 text-center text-xs text-ink/45">
                                {t.contact.form.privacy}
                            </p>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
